"use client"
import {
  Toaster as ArkToaster,
  createToaster,
  Toast,
} from "@ark-ui/react/toast"
import type { ComponentProps, JSX } from "react"
import { createPortal } from "react-dom"
import { styled } from "styled-system/jsx"

const Root = styled(Toast.Root, {
  base: {
    alignItems: "start",
    borderRadius: "md",
    color: "text",
    background: "secondary",
    display: "grid",
    minWidth: "[240px]",
    boxShadow:
      "[0px 0px 1px 0px #0000000A, 0px 2px 6px 0px #0000000A, 0px 10px 20px 0px #0000000A]",
    padding: "md",
    transformOrigin: "var(--transform-origin)",
    _open: {
      animationName: "[scale-in, fade-in]",
      animationDuration: "fast",
    },
    _closed: {
      animationName: "[scale-out, fade-out]",
      animationDuration: "fast",
    },
    transitionDuration: "slow",
    transitionProperty: "[translate]",
    translate: "[var(--x) var(--y)]",
    willChange: "translate",
    "&[data-type='success']": {
      backgroundColor: "success",
      color: "text.onDark",
    },
    "&[data-type='error']": {
      backgroundColor: "error",
      color: "text.onDark",
    },
    "&[data-type='warning']": {
      backgroundColor: "warning",
      color: "text.onDark",
    },
  },
})
const Title = styled(Toast.Title, {
  base: {
    color: "[inherit]",
    textStyle: "heading",
  },
})
const Description = styled(Toast.Description)
const StyledToaster = styled(ArkToaster)

export type ToastRootProps = ComponentProps<typeof Root>
export type ToastTitleProps = ComponentProps<typeof Title>
export type ToastDescriptionProps = ComponentProps<typeof Description>

export const toaster = createToaster({
  placement: "bottom",
  overlap: false,
  pauseOnPageIdle: true,
})

export const Toaster = (): JSX.Element => {
  return createPortal(
    <StyledToaster
      toaster={toaster}
      zIndex="toast"
      gap="sm"
      insetInlineEnd="md"
      insetBlockEnd="md"
    >
      {(toastItem) => (
        <Root data-type={toastItem.type}>
          {toastItem.title ? <Title>{toastItem.title}</Title> : undefined}
          {toastItem.description ? (
            <Description>{toastItem.description}</Description>
          ) : undefined}
        </Root>
      )}
    </StyledToaster>,
    document.body,
  )
}
